import { useInstanceStore } from '@/store/backendInstances'
import axios from 'axios'
import { DateTime } from 'luxon'
import { Buffer } from 'buffer'
import { nextTick, reactive } from 'vue'
import TinyQueue from 'tinyqueue'
import { useSettingsStore } from '@/store/settings'
import { useLogsStore } from '@/store/logs'
import { useEphemeralBackendInstancesStore } from '@/store/ephemeralBackendInstances.js'

/**
 * Handles Communication with a specific VMS Backend Instance. This class manages Connection Checks,
 * Connection Strategy Selection, Authentication, Request Queueing and Error Handling.
 */
class BackendAPI {
  constructor(globalUniqueIdentifier, { minimumFullConnectionCheckInterval = 1000 * 30, testMode = false }) {
    this.maximumConcurrentRequests = 6
    this.globalUniqueIdentifier = globalUniqueIdentifier
    this.requestsWaitingForAuthentication = []
    this.requestsWaitingForHost = []
    this.requestQueue = new TinyQueue([], (a, b) => a.priority - b.priority)
    this.testMode = testMode
    this.fullCheckPromise = null
    this.quickCheckPromise = null
    this.minimumFullConnectionCheckInterval = minimumFullConnectionCheckInterval
    this.currentlyPerformingFullCheck = false
    this.currentlyPerformingQuickCheck = false
    this.lastFullConnectionCheck = null
    this.discoveryObject = useInstanceStore().instances[this.globalUniqueIdentifier].discoveryObject
    this.lastAccessTokenRefresh = 0
    this.currentlyRefreshingAccessToken = false

    this.accessTokenRefreshPromise = null

    this.quickConnectionCheckIntervalWhileOnline = 1000 * 60 * 2
    this.fullConnectionCheckIntervalWhileOnline = 1000 * 60 * 10
    this.fullConnectionCheckIntervalWhileOffline = 1000 * 60 * 5

    this.timeWithoutUserInteractionBeforeMarkingStale = 1000 * 60 * 10
    this.lastUserInteractionTimestamp = Date.now()
    this.maxRetries = 3
    this.accessibleState = reactive({
      isOnline: false,
      loggedIn: false,
      accessToken: null,
      accountId: null,
      connectionStrategy: null,
      concurrentRequests: 0,
    })
    this.currentConnectionStrategy = undefined
  }

  /**
   * Initialize the Instance
   */
  initialize() {
    this.updateCurrentConnectionStrategy(null, true)
    this.validateRefreshToken()
    this.triggerScheduledFullConnectionCheck()
      .then()
      .catch(e => {
        useLogsStore().addLogEntry({
          message: 'Failed to perform initial full connection check',
          tag: 'backendAPI',
          globalUniqueIdentifier: this.globalUniqueIdentifier,
          level: 'ERROR',
          error: e,
        })
      })
  }

  /**
   * Triggers a Quick Connection Check (Only the currently selected Connection Strategy)
   * @returns {Promise<null>}
   */
  async triggerScheduledQuickConnectionCheck() {
    if (!this.currentlyPerformingQuickCheck) {
      this.quickCheckPromise = this.quickConnectionCheck()
    }
    return this.quickCheckPromise
  }

  /**
   * Triggers a Full Connection Check (All Connection strategies)
   **/
  async triggerScheduledFullConnectionCheck() {
    if (!this.currentlyPerformingFullCheck) {
      this.fullCheckPromise = this.fullConnectionCheck()
    }
    return this.fullCheckPromise
  }

  /**
   * Updates the Current Connection Strategy and sets the Online Status accordingly.
   * @param connectionStrategy {string|null} - The new Connection Strategy to use, or null if offline.
   * @param suppressLogs {boolean} - If true, suppresses log messages for this update.
   */
  updateCurrentConnectionStrategy(connectionStrategy, suppressLogs = false) {
    const onlineStatusChanged = this.currentConnectionStrategy !== connectionStrategy
    this.currentConnectionStrategy = connectionStrategy
    this.accessibleState.connectionStrategy = connectionStrategy
    this.accessibleState.isOnline = connectionStrategy !== null
    if (!onlineStatusChanged) return
    if (this.accessibleState.isOnline) {
      if (!suppressLogs)
        useLogsStore().addLogEntry({
          message: 'Server was detected as Online',
          globalUniqueIdentifier: this.globalUniqueIdentifier,
          tag: 'backendAPI',
          level: 'DEBUG',
        })
    } else {
      if (!suppressLogs)
        useLogsStore().addLogEntry({
          message: 'Server was detected as Offline!',
          globalUniqueIdentifier: this.globalUniqueIdentifier,
          tag: 'backendAPI',
          level: 'DEBUG',
        })
    }
    clearInterval(this.quickOnlineCheckInterval)
    clearInterval(this.fullOnlineCheckInterval)
    clearInterval(this.fullOfflineCheckInterval)

    if (connectionStrategy === null) {
      this.fullOfflineCheckInterval = setInterval(this.triggerScheduledFullConnectionCheck.bind(this), this.fullConnectionCheckIntervalWhileOffline)
    } else {
      this.quickOnlineCheckInterval = setInterval(this.triggerScheduledQuickConnectionCheck.bind(this), this.quickConnectionCheckIntervalWhileOnline)
      this.fullOnlineCheckInterval = setInterval(this.triggerScheduledFullConnectionCheck.bind(this), this.fullConnectionCheckIntervalWhileOnline)
    }
  }

  /**
   * Tests the connectivity for a given URL (Strategy)
   * @param url {URL} - The URL to test connectivity against.
   * @param signal {AbortSignal} - An optional AbortSignal to cancel the request.
   * @returns {Promise<(boolean|*|string)[]|(boolean|string)[]|(boolean|*)[]>}
   */
  async testConnectivity(url, signal) {
    if (!url) return [false, '', 'Strategy not used!']
    try {
      const res = await axios.get(`${url.href}api/p`, { timeout: 10000, signal: signal })
      if (res.status >= 200 && res.status < 300) {
        return [true, url, '']
      }
      return [false, url, `Server did not return the expected Result! ${res.status}`]
    } catch (e) {
      if (e.name === 'CanceledError' || signal?.aborted) {
        return [false, url, 'Aborted by higher-priority strategy']
      }
      return [false, url, e.message]
    }
  }

  /**
   * Returns the Local URL for the Backend Instance.
   * @returns {URL|null}
   */
  localUrl() {
    if (!this.discoveryObject.local) return null
    const localConfig = this.discoveryObject.local
    if (!localConfig.hostname || !localConfig.port || !localConfig.protocol) return null
    return new URL(`${this.discoveryObject.local.protocol}://${this.discoveryObject.local.hostname}:${this.discoveryObject.local.port}`)
  }

  /**
   * Returns the Remote URL for the Backend Instance.
   * @returns {URL|null}
   */
  remoteUrl() {
    if (!this.discoveryObject.remote) return null
    const remoteConfig = this.discoveryObject.remote
    if (!remoteConfig.hostname || !remoteConfig.port || !remoteConfig.protocol) return null
    return new URL(`${this.discoveryObject.remote.protocol}://${this.discoveryObject.remote.hostname}:${this.discoveryObject.remote.port}`)
  }

  /**
   * Returns the Gateway URL for the Backend Instance.
   * @returns {URL|null}
   */
  gatewayUrl() {
    if (!this.discoveryObject.gateway) return null
    const gatewayConfig = this.discoveryObject.gateway
    if (!gatewayConfig.id || !gatewayConfig.hostname || !gatewayConfig.port || !gatewayConfig.protocol) return null
    return new URL(
      `${this.discoveryObject.gateway.protocol}://${this.discoveryObject.gateway.hostname}:${this.discoveryObject.gateway.port}/gw/${this.discoveryObject.gateway.id}/`
    )
  }

  /**
   * Accepts the EULA for the Backend Instance.
   * @param version {string|undefined} - The version of the EULA to accept, or undefined for the latest version.
   * @returns {Promise<boolean|undefined|*>} - Returns true if the EULA was accepted, false if a refresh is required,
   * or undefined if an error occurred.
   */
  async acceptEULA(version) {
    return this.fetchAccessToken(version)
  }

  /**
   * Returns the Refresh Token for the Backend Instance.
   * @returns {*|string|null} - The Refresh Token if available, otherwise null.
   */
  getRefreshToken() {
    return useInstanceStore().instances[this.globalUniqueIdentifier]?.refreshToken
  }

  /**
   * Validates the Refresh Token and tries to log in if possible.
   * @returns {Promise<boolean>} - Returns true if the Refresh Token is valid and the user is
   * logged in, false otherwise.
   */
  async validateRefreshTokenAndTryToLoginIfPossible() {
    if (this.validateRefreshToken()) return true
    await useInstanceStore().autoLoginIntoInstanceIfPossible(this.globalUniqueIdentifier)
    return this.validateRefreshToken()
  }

  /**
   * (locally) Validates the Refresh Token.
   * @returns {boolean} - Returns true if the Refresh Token is valid, false otherwise.
   */
  validateRefreshToken() {
    const token = this.getRefreshToken()
    if (!token) {
      this.accessibleState.loggedIn = false
      useLogsStore().addLogEntry({
        message: 'No Refresh Token Found',
        tag: 'backendAPI',
        globalUniqueIdentifier: this.globalUniqueIdentifier,
        level: 'DEBUG',
      })
      return false
    }
    try {
      const decodedToken = JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString())
      if (!decodedToken.refresh || decodedToken.exp * 1000 <= Date.now()) {
        this.accessibleState.loggedIn = false
        return false
      }
      this.accessibleState.loggedIn = true
      this.accessibleState.accountId = decodedToken.uid
      useLogsStore().addLogEntry({
        message: 'Refresh Token Validated',
        tag: 'backendAPI',
        globalUniqueIdentifier: this.globalUniqueIdentifier,
        level: 'DEBUG',
      })
      return true
    } catch (e) {
      useLogsStore().addLogEntry({
        message: 'Failed to validate refresh token',
        error: e,
        tag: 'backendAPI',
        globalUniqueIdentifier: this.globalUniqueIdentifier,
        level: 'ERROR',
      })
      this.accessibleState.loggedIn = false
      return false
    }
  }

  /**
   * Fetches a new Access Token from the Backend Instance.
   * @param acceptedEULAVersion {string|undefined} - The version of the EULA to accept, or undefined
   * for the latest version.
   * @param attempts {number} - The number of attempts made to fetch the Access Token.
   * @returns {Promise<boolean|*|undefined>} - Returns true if the Access Token was fetched successfully,
   * false if a refresh (new refresh token) is required,
   */
  async fetchAccessToken(acceptedEULAVersion = undefined, attempts = 0) {
    if (!(await this.validateRefreshTokenAndTryToLoginIfPossible())) {
      return false
    }
    const host = await this.getHost()
    if (!host) {
      return false
    }
    const instanceStore = useInstanceStore()
    const refreshToken = instanceStore.instances[this.globalUniqueIdentifier].refreshToken

    const accessTokenRefreshAxiosConfig = {
      baseURL: host.href,
      url: 'api/login/refresh',
      method: 'POST',
      data: { refreshToken, acceptEula: acceptedEULAVersion || undefined },
      validateStatus: () => true,
    }
    try {
      const response = await axios(accessTokenRefreshAxiosConfig)
      if (response.status === 451) {
        useLogsStore().addLogEntry({
          message: 'EULA Refresh Required',
          tag: 'backendAPI',
          globalUniqueIdentifier: this.globalUniqueIdentifier,
          level: 'INFO',
        })
        useEphemeralBackendInstancesStore().eulaRefreshRequired[this.globalUniqueIdentifier] = true

        return false
      } else {
        useEphemeralBackendInstancesStore().eulaRefreshRequired[this.globalUniqueIdentifier] = false
      }
      if (response.status === 200) {
        this.accessibleState.accessToken = response.data.accessToken
        try {
          const permissionAxiosConfig = {
            baseURL: host.href,
            url: 'api/permissions',
            method: 'GET',
            headers: { Authorization: `Bearer ${this.accessibleState.accessToken}` },
            validateStatus: () => true,
          }
          const permissionResponse = await axios(permissionAxiosConfig)
          if (permissionResponse.status === 200) {
            useInstanceStore().updateInstance(this.globalUniqueIdentifier, 'availablePermissions', permissionResponse.data)
          }
        } catch (e) {
          useLogsStore().addLogEntry({
            message: 'Failed to fetch permissions',
            error: e,
            tag: 'backendAPI',
            globalUniqueIdentifier: this.globalUniqueIdentifier,
            level: 'ERROR',
          })
        }
        return this.validateRefreshTokenAndTryToLoginIfPossible()
      }
      if (response.status === 403) {
        delete useInstanceStore().instances[this.globalUniqueIdentifier].refreshToken
        return this.validateRefreshTokenAndTryToLoginIfPossible()
      }
    } catch (e) {
      if (attempts < 3) {
        await nextTick()
        return this.fetchAccessToken(acceptedEULAVersion, attempts + 1)
      }
      useLogsStore().addLogEntry({
        message: 'Failed to refresh access token',
        error: e,
        tag: 'backendAPI',
        globalUniqueIdentifier: this.globalUniqueIdentifier,
        level: 'ERROR',
      })
    }
  }

  async getNewAccessToken() {
    if (!this.accessTokenRefreshPromise) {
      this.accessTokenRefreshPromise = (async () => {
        try {
          const result = await this.fetchAccessToken()
          if (!result) {
            throw new Error('Failed to fetch a new Access Token!')
          }
          this.lastAccessTokenRefresh = DateTime.now().toMillis()
        } catch (e) {
          useLogsStore().addLogEntry({
            message: 'Failed to refresh access token',
            error: e,
            tag: 'backendAPI',
            globalUniqueIdentifier: this.globalUniqueIdentifier,
            level: 'ERROR',
          })
          throw e
        } finally {
          this.accessTokenRefreshPromise = null
        }
      })()
    }
    return this.accessTokenRefreshPromise
  }

  /**
   * Gets the current Host URL based on the current Connection Strategy.
   * @param skipConnectionCheck {boolean} - If true, skips triggering a new connection check.
   *   Used when the caller has already waited for a connection check to complete.
   * @returns {Promise<null|URL>} - Returns the Host URL if available, otherwise null.
   */
  async getHost(skipConnectionCheck = false) {
    const hasNoSelectedConnectionStrategy =
      !this.currentConnectionStrategy ||
      (useSettingsStore().forceConnectionStrategy && useSettingsStore().forcedConnectionStrategy !== this.currentConnectionStrategy)
    const hasNoRecentLastFullConnectionCheck =
      !this.lastFullConnectionCheck ||
      (this.lastFullConnectionCheck && this.lastFullConnectionCheck + this.minimumFullConnectionCheckInterval <= Date.now())
    if (hasNoSelectedConnectionStrategy && !skipConnectionCheck) {
      if (hasNoRecentLastFullConnectionCheck) {
        if (!this.currentlyPerformingFullCheck) {
          this.fullCheckPromise = this.fullConnectionCheck()
        }
      }
      await this.fullCheckPromise
    }
    switch (this.currentConnectionStrategy) {
      case 'local': {
        return this.localUrl()
      }
      case 'remote': {
        return this.remoteUrl()
      }
      case 'gateway': {
        return this.gatewayUrl()
      }
      default:
        return null
    }
  }

  /**
   * Performs a Quick Connection Check to see if the current Connection Strategy is still valid.
   */
  async quickConnectionCheck() {
    if (this.isInstanceStale()) {
      return
    }
    this.currentlyPerformingQuickCheck = true
    const abortController = new AbortController()
    try {
      let connectionPromise = null
      if (
        this.currentConnectionStrategy === 'local' &&
        (!useSettingsStore().forceConnectionStrategy || useSettingsStore().forcedConnectionStrategy === 'local')
      ) {
        connectionPromise = this.testConnectivity(this.localUrl(), abortController.signal)
      } else if (
        this.currentConnectionStrategy === 'gateway' &&
        (!useSettingsStore().forceConnectionStrategy || useSettingsStore().forcedConnectionStrategy === 'gateway')
      ) {
        connectionPromise = this.testConnectivity(this.gatewayUrl(), abortController.signal)
      } else if (
        this.currentConnectionStrategy === 'remote' &&
        (!useSettingsStore().forceConnectionStrategy || useSettingsStore().forcedConnectionStrategy === 'remote')
      ) {
        connectionPromise = this.testConnectivity(this.remoteUrl(), abortController.signal)
      }
      if (connectionPromise !== null) {
        const connectionResponse = await connectionPromise
        if (connectionResponse[0] === true) return
      }
      this.updateCurrentConnectionStrategy(null)
    } finally {
      this.currentlyPerformingQuickCheck = false
    }
  }

  /**
   * Performs a Full Connection Check to test all Connection Strategies.
   * Launches all applicable checks concurrently. Abort signals ensure lower-priority
   * strategies are cancelled once a higher-priority one succeeds (local > gateway > remote).
   */
  async fullConnectionCheck() {
    if (this.isInstanceStale()) return
    this.currentlyPerformingFullCheck = true
    const remoteController = new AbortController()
    const gatewayController = new AbortController()
    let localSuccessful = false
    let gatewaySuccessful = false
    let remoteSuccessful = false
    const promises = []

    const settingsStore = useSettingsStore()
    const forceStrategy = settingsStore.forceConnectionStrategy
    const forcedStrategy = settingsStore.forcedConnectionStrategy

    if (!forceStrategy || forcedStrategy === 'local') {
      promises.push(
        this.testConnectivity(this.localUrl())
          .then(result => {
            if (result[0] === true) {
              localSuccessful = true
              gatewayController.abort()
              remoteController.abort()
              this.updateCurrentConnectionStrategy('local')
            }
          })
          .catch(e => {
            useLogsStore().addLogEntry({
              message: 'Local Connection Check crashed',
              level: 'ERROR',
              error: e,
              tag: 'backendAPI',
              globalUniqueIdentifier: this.globalUniqueIdentifier,
            })
          })
      )
    }

    if (!forceStrategy || forcedStrategy === 'gateway') {
      promises.push(
        this.testConnectivity(this.gatewayUrl(), gatewayController.signal)
          .then(result => {
            if (result[0] === true) {
              gatewaySuccessful = true
              remoteController.abort()
              if (!localSuccessful) {
                this.updateCurrentConnectionStrategy('gateway')
              }
            }
          })
          .catch(e => {
            if (e.name !== 'CanceledError') {
              useLogsStore().addLogEntry({
                message: 'Gateway Connection Check crashed',
                level: 'ERROR',
                error: e,
                tag: 'backendAPI',
                globalUniqueIdentifier: this.globalUniqueIdentifier,
              })
            }
          })
      )
    }

    if (!forceStrategy || forcedStrategy === 'remote') {
      promises.push(
        this.testConnectivity(this.remoteUrl(), remoteController.signal)
          .then(result => {
            if (result[0] === true) {
              remoteSuccessful = true
              if (!localSuccessful && !gatewaySuccessful) {
                this.updateCurrentConnectionStrategy('remote')
              }
            }
          })
          .catch(e => {
            if (e.name !== 'CanceledError') {
              useLogsStore().addLogEntry({
                message: 'Remote Connection Check crashed',
                level: 'ERROR',
                error: e,
                tag: 'backendAPI',
                globalUniqueIdentifier: this.globalUniqueIdentifier,
              })
            }
          })
      )
    }
    if (promises.length) await Promise.allSettled(promises)
    this.lastFullConnectionCheck = Date.now()
    if (!localSuccessful && !remoteSuccessful && !gatewaySuccessful) {
      this.updateCurrentConnectionStrategy(null)
    }
    this.currentlyPerformingFullCheck = false
  }

  updateLastUserInteractionTimestamp() {
    this.lastUserInteractionTimestamp = Date.now()
  }

  isInstanceStale() {
    return Date.now() - this.lastUserInteractionTimestamp > this.timeWithoutUserInteractionBeforeMarkingStale
  }

  /**
   * Adds a new Request to the Request Queue and executes the queue.
   * @param config {Object} - The configuration for the request, including method, path, headers, etc.
   * @returns {BackendRequest} - Returns the created BackendRequest instance.
   */
  addRequest(config = {}) {
    if (config.automaticTriggeredRequest !== true) this.updateLastUserInteractionTimestamp()
    const request = new BackendRequest(config)
    this.requestQueue.push(request)
    this.executeQueue()
    return request
  }

  /**
   * Adds a batch of requests to the Request Queue.
   * @param configs {Array} - An array of request configurations to be added to the queue.
   * @returns {BackendRequest[]} - Returns an array of BackendRequest instances
   * created from the provided configurations.
   */
  addRequestBatch(configs = []) {
    if (configs.some(config => config.automaticTriggeredRequest !== true)) this.updateLastUserInteractionTimestamp()
    const requests = configs.map(config => new BackendRequest(config))
    for (const request of requests) {
      this.requestQueue.push(request)
    }
    this.executeQueue()
    return requests
  }

  /**
   * Finds the next request in the queue that can be executed.
   * @returns {BackendRequest} - Returns the next request that can be executed, or null if no requests are available.
   */
  findNextRequest() {
    if (this.requestsWaitingForAuthentication.length > 0 && !this.accessTokenRefreshPromise) {
      return this.requestsWaitingForAuthentication.shift()
    }
    if (this.requestsWaitingForHost.length > 0 && !this.currentlyPerformingFullCheck) {
      return this.requestsWaitingForHost.shift()
    }
    return this.requestQueue.length > 0 ? this.requestQueue.pop() : null
  }

  /**
   * Evicts all requests that are bound to a specific path from the request queue.
   * @param path {string} - The path to which the requests are bound.
   */
  evictPathBoundRequests(path) {
    const keptRequests = []
    while (this.requestQueue.length > 0) {
      const request = this.requestQueue.pop()
      if (request.isPathBound && request.boundToPath === path) {
        request.abort()
      } else {
        keptRequests.push(request)
      }
    }
    this.requestQueue = new TinyQueue(keptRequests, (a, b) => a.priority - b.priority)

    this.requestsWaitingForAuthentication = this.requestsWaitingForAuthentication.filter(request => {
      if (request.isPathBound && request.boundToPath === path) {
        request.abort()
        return false
      }
      return true
    })

    this.requestsWaitingForHost = this.requestsWaitingForHost.filter(request => {
      if (request.isPathBound && request.boundToPath === path) {
        request.abort()
        return false
      }
      return true
    })
  }

  /**
   * Executes a request from the queue.
   * This method checks if the request requires authentication and if the access token is available.
   * @param request {BackendRequest} - The request to be executed.
   * @returns {Promise<void>} - A promise that resolves when the request has been executed.
   */
  async executeRequest(request) {
    if (request.abortController?.signal?.aborted) {
      return request.reject(new Error('CanceledError'), this.globalUniqueIdentifier, request.path)
    }
    try {
      if (
        (request.requiresAuth && !this.accessibleState.accessToken) ||
        (request.forceReAuthentication === true && request.executionTimestamp > this.lastAccessTokenRefresh)
      ) {
        const tokenPromise = this.getNewAccessToken()
        const clonedRequest = request.clone()
        this.requestsWaitingForAuthentication.push(clonedRequest)
        tokenPromise
          .then(() => {
            nextTick(() => this.executeQueue())
          })
          .catch(e => {
            const index = this.requestsWaitingForAuthentication.findIndex(r => r === clonedRequest)
            if (index !== -1) {
              this.requestsWaitingForAuthentication.splice(index, 1)
              clonedRequest.reject(e, this.globalUniqueIdentifier, clonedRequest.path)
            }
            nextTick(() => this.executeQueue())
          })
        return
      }

      const hasNoSelectedConnectionStrategy =
        !this.currentConnectionStrategy ||
        (useSettingsStore().forceConnectionStrategy && useSettingsStore().forcedConnectionStrategy !== this.currentConnectionStrategy)
      const hasNoRecentLastFullConnectionCheck =
        !this.lastFullConnectionCheck ||
        (this.lastFullConnectionCheck && this.lastFullConnectionCheck + this.minimumFullConnectionCheckInterval <= Date.now())

      if (hasNoSelectedConnectionStrategy && !request._alreadyWaitedForHostCheck) {
        if (this.currentlyPerformingFullCheck || hasNoRecentLastFullConnectionCheck) {
          let checkPromise = this.fullCheckPromise
          if (!this.currentlyPerformingFullCheck) {
            checkPromise = this.triggerScheduledFullConnectionCheck()
          }
          if (checkPromise) {
            const clonedRequest = request.clone()
            clonedRequest._alreadyWaitedForHostCheck = true
            this.requestsWaitingForHost.push(clonedRequest)
            checkPromise.finally(() => {
              nextTick(() => this.executeQueue())
            })
            return
          }
        }
      }

      const host = await this.getHost(request._alreadyWaitedForHostCheck === true)
      if (!host) {
        return request.reject(new Error('No Host Available'), this.globalUniqueIdentifier, request.path)
      } else {
        const { needsAuthentication, maybeRetry, error } = await request.execute(host, this.globalUniqueIdentifier, this.accessibleState.accessToken)
        if (needsAuthentication) {
          const clonedRequest = request.clone()
          this.requestsWaitingForAuthentication.push(clonedRequest)
        }
        if (maybeRetry) {
          if (request.attempts < this.maxRetries) {
            const clonedRequest = request.clone()
            this.requestQueue.push(clonedRequest)
          } else {
            return request.reject(error, this.globalUniqueIdentifier, request.path)
          }
        }
      }
    } catch (e) {
      useLogsStore().addLogEntry({
        message: 'Failed to execute request',
        error: e,
        tag: 'backendAPI',
        globalUniqueIdentifier: this.globalUniqueIdentifier,
        level: 'ERROR',
      })
      return request.reject(e, this.globalUniqueIdentifier, request.path)
    }
  }

  /**
   * Executes the request queue by processing requests in the order of their priority.
   * This method checks if the number of concurrent requests is below the maximum limit
   * and executes the next request if available.
   */
  executeQueue() {
    while (this.accessibleState.concurrentRequests < this.maximumConcurrentRequests) {
      const nextRequest = this.findNextRequest()
      if (!nextRequest) return
      this.accessibleState.concurrentRequests++
      this.executeRequest(nextRequest).finally(() => {
        this.accessibleState.concurrentRequests--
        this.executeQueue()
      })
    }
  }

  /**
   * Destroys the BackendAPI instance, clearing all intervals and aborting any pending requests.
   * This method should be called when the instance is no longer needed to prevent memory leaks.
   */
  destroy() {
    clearInterval(this.quickOnlineCheckInterval)
    clearInterval(this.fullOnlineCheckInterval)
    clearInterval(this.fullOfflineCheckInterval)

    while (this.requestQueue.length > 0) {
      const request = this.requestQueue.pop()
      request.abort()
    }
    this.requestsWaitingForAuthentication.forEach(request => request.abort())
    this.requestsWaitingForAuthentication = []
    this.requestsWaitingForHost.forEach(request => request.abort())
    this.requestsWaitingForHost = []
  }

  /**
   * Logs out the user by deleting the refresh token from the instance store
   * and sending a logout request to the backend.
   * This method will also validate the refresh token after logging out.
   * This is used to ensure that the user is logged out
   * and that the refresh token is no longer valid.
   * @returns {Promise<void>}
   */
  async logOut() {
    this.addRequest({
      path: '/api/login/' + this.getRefreshToken(),
      method: 'DELETE',
      priority: 0,
      needsAuthentication: false,
    })
    delete useInstanceStore().instances[this.globalUniqueIdentifier].refreshToken
    this.validateRefreshToken()
  }

  /**
   * Performs a streaming request, bypassing the request queue. Reuses connection-strategy
   * host resolution and access-token handling, and returns the raw fetch Response so the
   * caller can read response.body as a stream.
   * @param config {Object} - The configuration for the request (path, method, data, headers, params, signal, requiresAuth, isPathBound, skipPermissionSimulation, automaticTriggeredRequest).
   * @returns {Promise<Response>} - The raw fetch Response.
   */
  async streamRequest(config = {}) {
    if (config.automaticTriggeredRequest !== true) this.updateLastUserInteractionTimestamp()
    const requiresAuth = config.requiresAuth !== false
    if (requiresAuth && !this.accessibleState.accessToken) {
      await this.getNewAccessToken()
    }
    return this._performStream(config, requiresAuth, false)
  }

  /**
   * Internal: builds and issues the fetch for a streaming request, refreshing the access token
   * once on a 401 and retrying.
   * @param config {Object}
   * @param requiresAuth {boolean}
   * @param isRetry {boolean}
   * @returns {Promise<Response>}
   */
  async _performStream(config, requiresAuth, isRetry) {
    const host = await this.getHost()
    if (!host) throw new Error('No Host Available')

    const headers = { ...(config.headers || {}) }
    if (requiresAuth) {
      headers.Authorization = `Bearer ${this.accessibleState.accessToken}`
      if (config.skipPermissionSimulation !== true && useSettingsStore().simulatePermissions) {
        headers['simulate-permissions'] = JSON.stringify(useSettingsStore().simulatedPermissions)
      }
    }
    headers['x-bound-path'] = config.isPathBound ? backendHandler.currentPath : 'none'

    const hasBody = config.data !== undefined && config.data !== null
    if (hasBody && !headers['Content-Type'] && !headers['content-type']) {
      headers['Content-Type'] = 'application/json'
    }

    const base = host.href.replace(/\/+$/, '')
    const rel = String(config.path || '').replace(/^\/+/, '')
    const url = new URL(base + '/' + rel)
    for (const [key, value] of Object.entries(config.params || {})) {
      url.searchParams.append(key, value)
    }

    const response = await fetch(url.href, {
      method: config.method || 'POST',
      headers,
      body: hasBody ? JSON.stringify(config.data) : undefined,
      signal: config.signal,
    })

    if (response.status === 401 && requiresAuth && !isRetry) {
      await this.getNewAccessToken()
      return this._performStream(config, requiresAuth, true)
    }
    return response
  }
}

class BackendRequest {
  constructor(config = {}, executionTimestamp) {
    this.requiresAuth = config.requiresAuth !== false
    this.isPathBound = config.isPathBound === true
    this.boundToPath = backendHandler.currentPath
    this.method = config.method ? config.method : 'GET'
    this.priority = config.priority ? config.priority : 5
    this.path = config.path ? config.path : ''
    this.skipPermissionSimulation = config.skipPermissionSimulation === true
    this.abortController = new AbortController()
    this.outsideAbortSignal = config.signal ? config.signal : null
    this._onOutsideAbort = null
    if (this.outsideAbortSignal) {
      this._onOutsideAbort = () => this.abortController.abort()
      this.outsideAbortSignal.addEventListener('abort', this._onOutsideAbort)
    }
    this.responseType = config.responseType ? config.responseType : 'json'
    this.onDownloadProgress = config.onDownloadProgress ? config.onDownloadProgress : null
    this.params = config.params ? config.params : []
    this.data = config.data ? config.data : null
    this.headers = config.headers ? config.headers : {}
    this.attempts = config.attempts ? config.attempts : 0
    this.mainPromise = new Promise((resolve, reject) => {
      this.promiseResolve = r => resolve(r)
      this.promiseReject = e => reject(e)
    })
    this.executionTimestamp = executionTimestamp
    this.forceReAuthentication = config.forceReAuthentication === true
  }

  /**
   * Aborts the request and cleans up any outside abort signal listeners.
   */
  abort() {
    this._removeOutsideAbortListener()
    this.abortController.abort()
  }

  _removeOutsideAbortListener() {
    if (this.outsideAbortSignal && this._onOutsideAbort) {
      this.outsideAbortSignal.removeEventListener('abort', this._onOutsideAbort)
      this._onOutsideAbort = null
    }
  }

  async execute(host, globalUniqueIdentifier, accessToken) {
    if (this.isPathBound && this.boundToPath !== backendHandler.currentPath) {
      this.reject(new Error('CanceledError'), globalUniqueIdentifier, this.path, this.requiresAuth, 'UNKNOWN')
      return {
        needsAuthentication: false,
        maybeRetry: false,
        error: new Error('CanceledError'),
      }
    }
    this.executionTimestamp = DateTime.now().toMillis()
    const headers = this.headers || {}
    let accessTokenAccountId = 'UNKNOWN'
    if (this.requiresAuth) {
      if (accessToken || useEphemeralBackendInstancesStore().eulaRefreshRequired[globalUniqueIdentifier]) {
        headers.Authorization = `Bearer ${accessToken}`
        try {
          const decodedToken = JSON.parse(Buffer.from(accessToken.split('.')[1], 'base64').toString())
          accessTokenAccountId = decodedToken.uid
        } catch (e) {
          useLogsStore().addLogEntry({
            message: 'Could not Decode Auth Token!',
            tag: 'backendRequest',
            error: e,
            globalUniqueIdentifier,
            level: 'ERROR',
          })
        }
      } else {
        this.attempts = this.attempts + 1
        this.forceReAuthentication = true
        return { needsAuthentication: true, maybeRetry: false }
      }
      if (this.skipPermissionSimulation !== true && useSettingsStore().simulatePermissions) {
        headers['simulate-permissions'] = JSON.stringify(useSettingsStore().simulatedPermissions)
      }
    }
    headers['x-bound-path'] = this.isPathBound ? this.boundToPath : 'none'
    const axiosConfig = {
      signal: this.abortController.signal,
      baseURL: host.href,
      url: this.path,
      headers: headers,
      params: this.params,
      responseType: this.responseType,
      method: this.method,
      data: this.data,
      validateStatus: () => true,
      onDownloadProgress: this.onDownloadProgress,
    }
    try {
      if (this.abortController.signal.aborted || (this.outsideAbortSignal && this.outsideAbortSignal.aborted)) {
        this.reject(new Error('CanceledError'), globalUniqueIdentifier, this.path, this.requiresAuth, accessTokenAccountId)
        return { needsAuthentication: false, maybeRetry: false, error: new Error('CanceledError') }
      }
      this.attempts = this.attempts + 1
      const axiosResponse = await axios(axiosConfig)
      if (!axiosResponse) throw new Error('No Response')
      if (this.requiresAuth && axiosResponse.status !== 451 && axiosResponse.status !== 401) {
        useEphemeralBackendInstancesStore().eulaRefreshRequired[globalUniqueIdentifier] = false
      }

      if (axiosResponse.status >= 200 && axiosResponse.status < 300) {
        this.resolve(axiosResponse, globalUniqueIdentifier, this.path, this.requiresAuth, accessTokenAccountId)
        return { needsAuthentication: false, maybeRetry: false }
      }

      if (axiosResponse.status === 401) {
        if (this.requiresAuth && !this.forceReAuthentication) {
          this.forceReAuthentication = true
          return { needsAuthentication: true, maybeRetry: false }
        }
        this.reject(new Error('Authentication failed'), globalUniqueIdentifier, this.path, this.requiresAuth, accessTokenAccountId)
        return { needsAuthentication: false, maybeRetry: false }
      }

      if (axiosResponse.status === 451) {
        useEphemeralBackendInstancesStore().eulaRefreshRequired[globalUniqueIdentifier] = true
        this.reject(new Error('EULA Refresh Required'), globalUniqueIdentifier, this.path, this.requiresAuth, accessTokenAccountId)
        return { needsAuthentication: false, maybeRetry: false }
      }

      this.resolve(axiosResponse, globalUniqueIdentifier, this.path, this.requiresAuth, accessTokenAccountId)
      return { needsAuthentication: false, maybeRetry: false }
    } catch (e) {
      if (e.name === 'CanceledError') {
        this.reject(e, globalUniqueIdentifier, this.path, this.requiresAuth, accessTokenAccountId)
        return { needsAuthentication: false, maybeRetry: false, error: e }
      }
      return { needsAuthentication: false, maybeRetry: true, error: e }
    }
  }

  resolve(response, globalUniqueIdentifier, path, requiresAuth, accountId) {
    this._removeOutsideAbortListener()
    const ok = response.status >= 200 && response.status < 300
    let error = undefined
    if (!ok) {
      error = new Error('Request Failed with Status Code ' + response.status)
      useLogsStore().addLogEntry({
        message: 'Request to ' + path + ' Failed with code ' + response.status + ', Requires Auth: ' + requiresAuth + ', Account: ' + accountId,
        tag: 'backendRequest',
        error: error,
        globalUniqueIdentifier,
        level: 'ERROR',
        httpMethod: this.method,
      })
    }
    this.promiseResolve({
      ok: response.status >= 200 && response.status < 300,
      data: response.data,
      response: response,
      status: response.status,
      attempts: this.attempts,
      error: error,
    })
  }

  reject(error, globalUniqueIdentifier = null, path, requiresAuth, accountId) {
    this._removeOutsideAbortListener()
    const aborted = error.name === 'CanceledError' || error.message === 'CanceledError'
    if (!aborted) {
      useLogsStore().addLogEntry({
        message: '[' + path + '] : Request Failed , Requires Auth: ' + requiresAuth + ', Account: ' + accountId,
        error: error,
        tag: 'backendRequest',
        globalUniqueIdentifier,
        level: 'ERROR',
        httpMethod: this.method,
      })
    }
    this.promiseResolve({
      ok: false,
      error: error,
      data: null,
      attempts: this.attempts,
      aborted: aborted,
    })
  }

  clone() {
    const clonedRequest = new BackendRequest({}, this.executionTimestamp)

    this._removeOutsideAbortListener()

    Object.assign(clonedRequest, this)

    if (this.isPathBound) {
      clonedRequest.isPathBound = true
      clonedRequest.boundToPath = this.boundToPath
    }
    clonedRequest.mainPromise = this.mainPromise
    clonedRequest.promiseResolve = this.promiseResolve
    clonedRequest.promiseReject = this.promiseReject

    clonedRequest.abortController = new AbortController()
    clonedRequest._onOutsideAbort = null

    if (clonedRequest.outsideAbortSignal) {
      if (clonedRequest.outsideAbortSignal.aborted) {
        clonedRequest.abortController.abort()
      } else {
        clonedRequest._onOutsideAbort = () => clonedRequest.abortController.abort()
        clonedRequest.outsideAbortSignal.addEventListener('abort', clonedRequest._onOutsideAbort)
      }
    }

    return clonedRequest
  }
}

class BackendHandler {
  constructor() {
    this.globalRequests = []
    this.instances = {}
    this.settings = {}
    this.currentPath = '/'
  }

  triggerRouteChange(oldPath, newPath) {
    this.currentPath = newPath
    for (const instance of Object.values(this.instances)) {
      instance.evictPathBoundRequests(oldPath)
    }
  }

  updateSettings(settings = {}) {
    this.settings = settings
  }

  getOrCreateBackendAPIInstance(globalUniqueIdentifier) {
    if (useInstanceStore().instances[globalUniqueIdentifier] === undefined) {
      const e = new Error('Instance not found in InstanceStore')
      useLogsStore().addLogEntry({
        message: 'Instance not found: ' + globalUniqueIdentifier,
        tag: 'backendHandler',
        globalUniqueIdentifier: null,
        error: e,
        level: 'ERROR',
      })
      throw e
    }
    if (this.instances[globalUniqueIdentifier] === undefined) {
      this.instances[globalUniqueIdentifier] = new BackendAPI(globalUniqueIdentifier, this.settings)
      this.instances[globalUniqueIdentifier].initialize()
    }
    this.instances[globalUniqueIdentifier].updateLastUserInteractionTimestamp()
    return this.instances[globalUniqueIdentifier]
  }

  makeRequest(globalUniqueIdentifier = undefined, config = {}) {
    if (globalUniqueIdentifier === undefined) {
      globalUniqueIdentifier = useInstanceStore().selectedInstanceId
    }
    if (!globalUniqueIdentifier) {
      throw new Error('No Instance Selected')
    }
    const backendAPI = this.getOrCreateBackendAPIInstance(globalUniqueIdentifier)
    const request = backendAPI.addRequest(config)
    return request.mainPromise
  }

  get(globalUniqueIdentifier = undefined, config = {}) {
    return this.makeRequest(globalUniqueIdentifier, { ...config, method: 'GET' })
  }

  post(globalUniqueIdentifier = undefined, config = {}) {
    return this.makeRequest(globalUniqueIdentifier, { ...config, method: 'POST' })
  }

  put(globalUniqueIdentifier = undefined, config = {}) {
    return this.makeRequest(globalUniqueIdentifier, { ...config, method: 'PUT' })
  }

  delete(globalUniqueIdentifier = undefined, config = {}) {
    return this.makeRequest(globalUniqueIdentifier, { ...config, method: 'DELETE' })
  }

  patch(globalUniqueIdentifier = undefined, config = {}) {
    return this.makeRequest(globalUniqueIdentifier, { ...config, method: 'PATCH' })
  }

  makeStreamRequest(globalUniqueIdentifier = undefined, config = {}) {
    if (globalUniqueIdentifier === undefined) {
      globalUniqueIdentifier = useInstanceStore().selectedInstanceId
    }
    if (!globalUniqueIdentifier) {
      throw new Error('No Instance Selected')
    }
    const backendAPI = this.getOrCreateBackendAPIInstance(globalUniqueIdentifier)
    return backendAPI.streamRequest(config)
  }

  postStream(globalUniqueIdentifier = undefined, config = {}) {
    return this.makeStreamRequest(globalUniqueIdentifier, { ...config, method: 'POST' })
  }

  makeRequestBatchForSingleInstance(globalUniqueIdentifier, configs = []) {
    const backendAPI = this.getOrCreateBackendAPIInstance(globalUniqueIdentifier)
    const requests = backendAPI.addRequestBatch(configs)
    return requests.map(request => request.mainPromise)
  }

  async makeGlobalRequest(config) {
    const axiosConfig = {
      baseURL: config.hostname,
      url: config.path,
      headers: config.headers,
      params: config.params,
      responseType: config.responseType,
      method: config.method ? config.method : 'GET',
      data: config.data,
      validateStatus: () => true,
    }
    try {
      const axiosResponse = await axios(axiosConfig)
      if (!axiosResponse) throw new Error('No Response')
      return {
        ok: axiosResponse.status >= 200 && axiosResponse.status < 300,
        data: axiosResponse.data,
        response: axiosResponse,
        status: axiosResponse.status,
      }
    } catch (error) {
      return { ok: false, error: error, data: null }
    }
  }

  async logOutOfInstance(globalUniqueIdentifier) {
    const instance = this.getOrCreateBackendAPIInstance(globalUniqueIdentifier)
    await instance.logOut()
  }

  destroyInstances() {
    for (const instance of Object.keys(this.instances)) {
      this.instances[instance].destroy()
      delete this.instances[instance]
    }
  }

  removeInstance(globalUniqueIdentifier) {
    if (this.instances[globalUniqueIdentifier]) {
      this.instances[globalUniqueIdentifier].destroy()
      delete this.instances[globalUniqueIdentifier]
    }
  }
}

const backendHandler = new BackendHandler()
export default backendHandler
